import { useState, useEffect } from 'react'
import { useToast } from '../../../lib/ToastContext'
import { db, storage } from '../../../firebase'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

/**
 * Hook for managing products
 */
export function useProducts() {
  const [products, setProducts] = useState([])
  const [recentProducts, setRecentProducts] = useState([])
  const [priceAlerts, setPriceAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const toast = useToast()

  // Subscribe to recent products (last 20)
  useEffect(() => {
    const q = query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc'),
      limit(20)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const prods = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setRecentProducts(prods)
      setLoading(false)
    }, (err) => {
      console.error('Failed to load products:', err)
      toast.error('Failed to load products')
      setError('Failed to load products')
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // Subscribe to products with outdated prices (30+ days)
  useEffect(() => {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    const q = query(
      collection(db, 'products'),
      where('priceUpdatedAt', '<', Timestamp.fromDate(thirtyDaysAgo)),
      where('status', '==', 'active'),
      orderBy('priceUpdatedAt', 'asc'),
      limit(20)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const prods = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setPriceAlerts(prods)
    }, (err) => {
      // This query might fail if index doesn't exist, that's ok
      console.warn('Price alerts query failed:', err)
    })

    return () => unsubscribe()
  }, [])

  // Upload image to Firebase Storage
  const uploadImage = async (file, productId) => {
    const timestamp = Date.now()
    const fileName = `${productId || 'temp'}_${timestamp}_${file.name}`
    const storageRef = ref(storage, `products/${fileName}`)

    try {
      await uploadBytes(storageRef, file)
      const url = await getDownloadURL(storageRef)
      return { url, fileName }
    } catch (err) {
      console.error('Image upload failed:', err)
      toast.error('Image upload failed — please try again')
      throw err
    }
  }

  // Create new product
  const createProduct = async (productData, imageFile) => {
    try {
      // Upload image first
      let imageUrl = null
      let imageName = null

      if (imageFile) {
        const tempId = `temp_${Date.now()}`
        const { url, fileName } = await uploadImage(imageFile, tempId)
        imageUrl = url
        imageName = fileName
      }

      // Create product document
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        initialImage: imageUrl,
        initialImageName: imageName,
        images: imageUrl ? [{ url: imageUrl, name: imageName, isInitial: true }] : [],
        status: 'pending_photos', // pending_photos, active, sold, archived
        publishedTo: ['pos'], // Only POS initially
        shopifyId: null,
        shopifyPublished: false,
        priceUpdatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      return docRef.id
    } catch (err) {
      console.error('Failed to create product:', err)
      toast.error('Failed to create product — please try again')
      throw err
    }
  }

  // Update product
  const updateProduct = async (productId, updates) => {
    try {
      const docRef = doc(db, 'products', productId)
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (err) {
      console.error('Failed to update product:', err)
      toast.error('Failed to update product — please try again')
      throw err
    }
  }

  // Update product price
  const updatePrice = async (productId, costPrice, salePrice) => {
    try {
      const docRef = doc(db, 'products', productId)
      await updateDoc(docRef, {
        costPrice,
        salePrice,
        priceUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    } catch (err) {
      console.error('Failed to update price:', err)
      toast.error('Failed to update price — please try again')
      throw err
    }
  }

  // Update product status
  const updateProductStatus = async (productId, newStatus) => {
    try {
      const docRef = doc(db, 'products', productId)
      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      })
    } catch (err) {
      console.error('Failed to update product status:', err)
      toast.error('Failed to update product status')
      throw err
    }
  }

  // Delete product
  const deleteProduct = async (productId) => {
    try {
      await deleteDoc(doc(db, 'products', productId))
    } catch (err) {
      console.error('Failed to delete product:', err)
      const isPermissionError = err?.code === 'permission-denied'
      toast.error(
        isPermissionError
          ? 'Permission denied to delete this product'
          : 'Failed to delete product — please try again'
      )
      throw err
    }
  }

  return {
    products,
    recentProducts,
    priceAlerts,
    loading,
    error,
    createProduct,
    updateProduct,
    updateProductStatus,
    updatePrice,
    deleteProduct,
    uploadImage
  }
}

export default useProducts
